import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { ArrowRight, MapPin, Clock, Users, Check, ArrowLeft } from 'lucide-react';

const WebDeveloper = () => {
  const jobDetails = {
    title: "Web Developer",
    department: "Engineering",
    location: "Remote / Perth",
    type: "Full-time",
    experience: "3-5 years",
    description: "Build scalable web applications using modern technologies. You'll be part of a team that values clean code, innovation, and creating solutions that make a real difference to Australian businesses and consumers.",
    responsibilities: [
      "Develop and maintain web applications using React, Node.js, and modern JavaScript",
      "Design and implement RESTful APIs and database schemas",
      "Collaborate with designers to create pixel-perfect, responsive user interfaces",
      "Write clean, testable, and maintainable code following best practices",
      "Participate in code reviews and maintain high code quality standards",
      "Work with cloud platforms and DevOps tools for deployment and scaling"
    ],
    requirements: [
      "3-5 years of web development experience",
      "Strong proficiency in React, Node.js, and modern JavaScript",
      "Experience with databases (PostgreSQL, MongoDB)",
      "Knowledge of cloud platforms (AWS, Azure)",
      "Understanding of DevOps practices and CI/CD",
      "Passion for writing clean, maintainable code",
      "Experience with version control (Git) and agile methodologies",
      "Strong problem-solving skills and attention to detail"
    ],
    whatWeOffer: [
      "Opportunity to work on cutting-edge projects like GoFella",
      "Flexible remote working options with Perth office access",
      "Professional development budget and conference attendance",
      "Collaborative team environment with experienced developers",
      "Chance to work with modern tech stack and latest tools"
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Back Navigation */}
      <section className="py-6 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/careers" className="inline-flex items-center text-vincotek-blue hover:text-blue-700 transition-colors">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Careers
          </Link>
        </div>
      </section>

      {/* Job Header */}
      <section className="py-12 bg-gradient-to-br from-vincotek-yellow to-yellow-500 text-vincotek-dark">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {jobDetails.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <Badge className="bg-vincotek-dark text-white px-3 py-1">
                {jobDetails.department}
              </Badge>
              <div className="flex items-center text-gray-700">
                <MapPin className="h-4 w-4 mr-2" />
                {jobDetails.location}
              </div>
              <div className="flex items-center text-gray-700">
                <Clock className="h-4 w-4 mr-2" />
                {jobDetails.type}
              </div>
              <div className="flex items-center text-gray-700">
                <Users className="h-4 w-4 mr-2" />
                {jobDetails.experience}
              </div>
            </div>

            <p className="text-xl text-gray-800 leading-relaxed">
              {jobDetails.description}
            </p>
          </div>
        </div>
      </section>

      {/* About the Role */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-vincotek-dark mb-8">
              About the Role
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-8">
              As a Web Developer at Vincotek, you'll work on exciting projects that impact real users across Australia. 
              From building our innovative GoFella platform to creating custom solutions for our clients, you'll be part 
              of a team that values technical excellence and meaningful impact.
            </p>
            
            <h3 className="text-2xl font-bold text-vincotek-dark mb-6">Key Responsibilities</h3>
            <div className="space-y-4">
              {jobDetails.responsibilities.map((responsibility, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <Check className="h-5 w-5 text-vincotek-blue flex-shrink-0 mt-1" />
                  <p className="text-gray-700 leading-relaxed">
                    {responsibility}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Skills & Experience */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-vincotek-dark mb-8">
              Skills & Experience
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {jobDetails.requirements.map((requirement, index) => (
                <Card key={index} className="border-0 shadow-md">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-vincotek-yellow rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-vincotek-dark font-bold text-sm">{index + 1}</span>
                      </div>
                      <p className="text-gray-700 leading-relaxed">
                        {requirement}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-vincotek-dark mb-8">
              What We Offer
            </h2>
            
            <div className="space-y-4">
              {jobDetails.whatWeOffer.map((offer, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-vincotek-blue rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed text-lg">
                    {offer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              How to Apply
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Excited to join our engineering team and build the future of digital products in Australia? 
              Submit your application with your portfolio, GitHub profile, and cover letter explaining why you'd be a great fit.
            </p>
            <Link to="/careers#application">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Apply for This Position
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebDeveloper;